import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectLanguage, selectTranslations } from 'state/reducers/translation.reducers';
import BasicTable from 'components/BasicTable';
import {
	getActivities,
	getActivityDates,
	setActiveDates,
	setNavbarSearchActiveDates,
	setLoading,
	loadingActivities,
} from 'state/reducers/activity.reducers';
import { ActivityEnum, ActivityTableColumns, ActivityTypes } from 'constants/activity.constants';
import { columnType, FilterType } from 'components/BasicTable/types';
import _ from 'lodash';
import { filterDates } from 'components/formElements/DateRange';
import { convertType } from 'constants/convert.constants';
import { pagesEnum, timing } from 'constants/ui.constants';
import { Box } from '@mui/material';
import { getMobile } from 'state/reducers/ui.reducers';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { downloadReport } from 'services/reports.services';
import { User } from 'constants/user.constants';
import { getUserDetails } from 'state/reducers/user.reducers';
import { BalanceDepositType, BalanceType, BalanceTypeTranslation } from 'constants/balance.constants';
import { SupportType } from 'constants/contact.constants';
import { useNavigate } from 'react-router-dom';
import { CustomLink } from 'theme/elements/Text';
import { getCountriesObj } from 'state/reducers/global.reducers';
import { downloadBlobResponse } from 'utils';
import PaymentConfirmationButton from './PaymentConfirmationButton';
export const getTypeByActivity = (item: any) => {
	if (_.isEqual(ActivityTypes.balanceActivities, item?.type)) {
		return _.isEqual(item?.balanceType, BalanceType.Topping)
			? BalanceTypeTranslation.Topping
			: BalanceTypeTranslation.Withdrawal;
	}
	return item?.type;
};

export const formatValues = (list: any[], t: any, countries: object | undefined = {}) => {
	const prefixFields = 'page.activity.table.';
	const feesObject = _.keyBy(list, 'feeId');
	let activities: any[] = [];
	list.forEach((item) => {
		let cl = _.clone(item);
		_.assign(cl, { paymentConfirmation: '' });
		_.assign(cl, { beneficiaryName: _.get(cl, 'beneficiary.companyName') });
		_.assign(cl, { beneficiaryCountry: _.get(countries, _.get(cl, 'beneficiary.countryId'))?.name });
		item.status && _.assign(cl, { status: t(prefixFields + 'status.' + _.snakeCase(item.status) + '.text') });
		_.assign(cl, { originType: getTypeByActivity(item) });
		const isDigitalTopping =
			cl.originType === ActivityTypes.topping && item.depositType === BalanceDepositType.Digital;

		_.assign(cl, {
			type: t(
				`${prefixFields}type.${isDigitalTopping ? 'digital_' : ''}${_.snakeCase(getTypeByActivity(item))}.text`
			),
		});
		if (!_.isEqual(item.type, ActivityTypes.conversionActivities)) {
			_.assign(cl, {
				accumulatedBalance: item?.accumulatedBalance ? item?.currency?.symbol + item.accumulatedBalance : '',
			});
		}
		if (_.isEqual(item.type, ActivityTypes.conversionActivities)) {
			_.assign(cl, {
				createdByContactName:
					item?.pendingQuote?.contact?.firstName + ' ' + item?.pendingQuote?.contact?.lastName,
			});
			_.assign(cl, {
				amount:
					'-' +
					item?.pendingQuote?.sellCurrency?.symbol +
					item.pendingQuote?.altshulerSellAmount.toLocaleString('en-US'),
			});
			_.assign(cl, {
				accumulatedBalance: item.accumulatedBalanceSellCurrency
					? item?.pendingQuote?.sellCurrency?.symbol + item.accumulatedBalanceSellCurrency
					: '',
			});
			_.assign(cl, { currencyCode: item?.pendingQuote?.sellCurrency?.code });
			activities.push(cl);
			let buy = _.clone(cl);
			_.assign(buy, {
				amount:
					'+' +
					item?.pendingQuote?.buyCurrency?.symbol +
					item.pendingQuote?.altshulerBuyAmount.toLocaleString('en-US'),
				accumulatedBalance: item.accumulatedBalanceBuyCurrency
					? item?.pendingQuote?.buyCurrency?.symbol + item.accumulatedBalanceBuyCurrency
					: '',
			});
			_.assign(buy, { currencyCode: item?.pendingQuote?.buyCurrency?.code });
			activities.push(buy);
		} else if (_.isEqual(ActivityTypes.paymentFeesActivities, item.type)) {
			_.assign(cl, { amount: '-' + item?.currency?.symbol + item.amount.toLocaleString('en-US') });
			_.assign(cl, { currencyCode: item?.currency?.code });
			if (_.has(feesObject, item.id)) {
				let parent = _.get(feesObject, item.id);
				_.assign(cl, {
					beneficiaryName: _.get(parent, 'beneficiary.companyName'),
					beneficiaryCountry: _.get(countries, _.get(parent, 'beneficiary.countryId'))?.name,
					reference: parent?.reference,
					settlementDate: parent?.settlementDate,
					//status: t(prefixFields + 'status.' + _.snakeCase(parent?.status || '') + '.text')
				});
			}
			activities.push(cl);
		} else if (_.isEqual(item.type, ActivityTypes.paymentActivities)) {
			_.assign(cl, { isPaymentRow: true, paymentStatusRaw: item.status });
			_.assign(cl, { amount: '-' + (item?.currency?.symbol || '') + item.amount.toLocaleString('en-US') });
			_.assign(cl, { currencyCode: item?.currency?.code || '' });
			_.assign(cl, {
				approvedByContactName: `${item?.signedByContact?.firstName} ${item?.signedByContact?.lastName}` || '',
			});
			activities.push(cl);
		} else {
			const isNegative = item?.balanceType === SupportType.Withdrawal;
			_.assign(cl, {
				amount: (isNegative ? '-' : '') + (item?.currency?.symbol || '') + item.amount.toLocaleString('en-US'),
			});
			_.assign(cl, { currencyCode: item?.currency?.code || '' });
			activities.push(cl);
		}
	});

	return activities;
};

export default function ActivitiesTable(props: any) {
	const user: User = useAppSelector(getUserDetails);
	const t = useAppSelector(selectTranslations);
	const activities = useAppSelector(getActivities);
	const activityDates = useAppSelector(getActivityDates);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const prefixFields = 'page.activity.table.';
	const isMobile = useAppSelector(getMobile);
	const filteredData = useRef([]);
	const [isnavbarSearchMode, setIsnavbarSearchMode] = useState(false);
	const [exportingPaymentIds, setExportingPaymentIds] = useState<number[]>([]);
	const [searchItem, setSearchItem] = useState([]);
	const lang = useAppSelector(selectLanguage);
	const countries = useAppSelector(getCountriesObj);
	const activitiesColumns = () => {
		let columns: columnType[] = [];
		const feesObject = _.keyBy(activities, 'feeId');
		_.keys(ActivityTableColumns).forEach((element) => {
			let props: columnType = {
				fieldId: ActivityEnum.reference,
				name: element,
				title: t(prefixFields + 'column.' + _.snakeCase(element) + '.text'),
				type: ActivityTableColumns[element],
				formatDate: _.isEqual(ActivityEnum.settlementDate, element) ? timing.DATE : undefined,
			};
			if (element === ActivityEnum.paymentConfirmation) {
				props.fieldId = 'id';
				props.sortable = false;
				props.hideHeaderLabel = true;
				props.customValueHandle = (_value, record) => renderPaymentConfirmationForRow(record);
			}
			if (element === ActivityEnum.reference) {
				props.customValueHandle = (value, record) => {
					if (record?.originType === ActivityTypes.paymentActivities) {
						return (
							<CustomLink onClick={() => navigate(`/${pagesEnum.PAY}/profile/${record.id}`)}>
								{value}
							</CustomLink>
						);
					}
					if (record?.originType === ActivityTypes.paymentFeesActivities && _.has(feesObject, record.id)) {
						return (
							<CustomLink
								onClick={() =>
									navigate(`/${pagesEnum.PAY}/profile/${_.get(feesObject, record.id)?.id}`)
								}
							>
								{value}
							</CustomLink>
						);
					}
					if (record?.originType === ActivityTypes.conversionActivities) {
						return (
							<CustomLink onClick={() => navigate(`/${pagesEnum.CONVERT}/profile/${record.id}`)}>
								{value}
							</CustomLink>
						);
					}
					if (record?.originType === ActivityTypes.withdrawal) {
						return (
							<CustomLink
								onClick={() => navigate(`/${pagesEnum.ACTIVITY}/profile/withdrawal/${record.id}`)}
							>
								{value}
							</CustomLink>
						);
					}
					if (record?.originType === ActivityTypes.topping) {
						return (
							<CustomLink onClick={() => navigate(`/${pagesEnum.ACTIVITY}/profile/topping/${record.id}`)}>
								{value}
							</CustomLink>
						);
					}
					if (record?.originType === ActivityTypes.balanceRefund) {
						return (
							<CustomLink onClick={() => navigate(`/${pagesEnum.REFUND}/profile/${record.id}`)}>
								{value}
							</CustomLink>
						);
					}
					return value;
					//navigate(`/${pagesEnum.REQUEST}/requestDetailsPY/${target.id}`);
				};
			}
			columns.push(props);
		});
		return columns;
	};

	const handleExportClick = async (paymentId: number, reference: string) => {
		setExportingPaymentIds((paymentIds) => [...paymentIds, paymentId]);
		try {
			const response: any = await dispatch(
				downloadReport({
					reportKey: 'payment_report',
					locale: 'en',
					format: 'pdf',
					filters: { paymentId },
				})
			);
			if (response) {
				downloadBlobResponse(response, `Payment-${reference}.pdf`);
			}
		} finally {
			setExportingPaymentIds((paymentIds) => paymentIds.filter((id) => id !== paymentId));
		}
	};

	const renderPaymentConfirmationForRow = (row: any) => {
		return (
			<PaymentConfirmationButton
				row={row}
				isMobile={isMobile}
				isExportBusy={exportingPaymentIds.includes(row.id)}
				label={t('page.activity.payment_confirmation.download.text')}
				disabledHint={t('page.activity.payment_confirmation.disabled_hint.text')}
				onExport={handleExportClick}
			/>
		);
	};

	useEffect(() => {
		if (props.actionReference) {
			setIsnavbarSearchMode(true);
			const itemToSearch = activities.filter((item: any) => item.reference === props.reference);
			setSearchItem(itemToSearch);
		}
	}, [isnavbarSearchMode]);
	return (
		<Box
			sx={{
				'& .table-wrap-stack': { display: 'flex', flexDirection: 'column', gap: 4 },
				'& .MuiTableCell-root:nth-of-type(4)': { direction: _.includes(lang, 'he') ? 'rtl' : 'ltr' },
			}}
		>
			<BasicTable
				fieldId="id"
				data={formatValues(activities, t, countries)}
				navbarSearchMode={isnavbarSearchMode}
				handleChangeOnClose={true}
				navbarSearchWord={props.reference}
				columns={activitiesColumns()}
				defaultSortBy={ActivityEnum.createdAt}
				includeSearchBox
				searchColumns={[ActivityEnum.reference, ActivityEnum.createdByContactName]}
				triggerFilteredData={(data) => {
					filteredData.current = data;
				}}
				filterList={{
					[ActivityEnum.createdAt]: {
						type: FilterType.dateRange,
						label: t(prefixFields + 'filter.created_at.text'),
						value: activityDates,
						handleChange: (dates: filterDates) => {
							dispatch(setNavbarSearchActiveDates(dates));
							dispatch(setActiveDates(dates));
						},
					},
					[ActivityEnum.currencyCode]: {
						type: FilterType.unique,
						label: t(prefixFields + 'filter.currency.text'),
						primaryOption: t(`${prefixFields}filter.currency.select_all.text`),
					},
					[ActivityEnum.type]: {
						type: FilterType.unique,
						label: t(prefixFields + 'filter.type.text'),
						primaryOption: t(`${prefixFields}filter.type.select_all.text`),
					},
					[ActivityEnum.status]: {
						type: FilterType.unique,
						label: t(prefixFields + 'filter.status.text'),
						primaryOption: t(`${prefixFields}filter.status.select_all.text`),
					},
				}}
			/>
		</Box>
	);
}
